import React, { useState, useEffect } from "react";
import { formatNumber } from "../lib/format";
import { useSettings } from "../SettingsContext";

// Text input that shows a formatted number (thousands separator + 2 decimals)
// when NOT focused, and the raw editable value while focused.
// Emits normalized string values via onChange (e.g. "1234.5" while typing,
// "1234.50" on blur).
const MoneyInput = ({
  value,
  onChange,
  allowNegative = true,
  className = "",
  placeholder = "0.00",
  ...rest
}) => {
  const { settings } = useSettings();
  const [focused, setFocused] = useState(false);
  const [draft, setDraft] = useState(value ?? "");

  // Keep local draft in sync when parent value changes and we're not editing
  useEffect(() => {
    if (!focused) setDraft(value ?? "");
  }, [value, focused]);

  const displayValue = () => {
    if (focused) return draft;
    if (draft === "" || draft == null) return "";
    const n = typeof draft === "number" ? draft : parseFloat(draft);
    if (isNaN(n)) return String(draft);
    const abs = formatNumber(Math.abs(n), settings);
    return n < 0 ? `-${abs}` : abs;
  };

  const sanitize = (raw) => {
    // Allow digits, one leading '-' (if allowed), and one '.'
    let s = String(raw);
    const neg = allowNegative && s.trim().startsWith("-");
    s = s.replace(/[^\d.]/g, "");
    const parts = s.split(".");
    if (parts.length > 2) s = parts[0] + "." + parts.slice(1).join("");
    return neg ? "-" + s : s;
  };

  const handleChange = (e) => {
    const clean = sanitize(e.target.value);
    setDraft(clean);
    onChange(clean);
  };

  const handleBlur = () => {
    setFocused(false);
    if (draft === "" || draft == null) {
      onChange("");
      return;
    }
    const n = typeof draft === "number" ? draft : parseFloat(draft);
    if (isNaN(n)) {
      onChange("");
      setDraft("");
      return;
    }
    const normalized = n.toFixed(2);
    setDraft(normalized);
    onChange(normalized);
  };

  return (
    <input
      type="text"
      inputMode="decimal"
      value={displayValue()}
      onChange={handleChange}
      onFocus={() => setFocused(true)}
      onBlur={handleBlur}
      placeholder={placeholder}
      className={className}
      {...rest}
    />
  );
};

export default MoneyInput;
